/**
 * ╔═══════════════════════════════════════════════════════════════╗
 * ║  ATELIER.JS  v3.0  —  CINEMATIC ENGINE                       ║
 * ║  Scroll is the timeline. Every section is a scene.            ║
 * ╚═══════════════════════════════════════════════════════════════╝
 */

(function(W, D) {
  'use strict';

  const lerp = (a, b, t) => a + (b - a) * t;
  const clamp = (v, a, b) => Math.min(Math.max(v, a), b);
  const map = (v, a, b, c, d) => clamp(c + (d - c) * ((v - a) / (b - a)), Math.min(c,d), Math.max(c,d));
  const rand = (a, b) => Math.random() * (b - a) + a;
  const isMobile = () => W.innerWidth <= 749 || 'ontouchstart' in W;
  const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&';

  function observe(el, cb, threshold, margin) {
    const o = new IntersectionObserver(entries => {
      entries.forEach(e => { if (e.isIntersecting) { cb(e); o.unobserve(el); } });
    }, { threshold: threshold || 0.08, rootMargin: margin || '0px 0px -40px 0px' });
    o.observe(el);
  }

  // ─── SMOOTH SCROLL ────────────────────────────────────────────────────────
  const Smooth = {
    y: 0, ty: 0, ease: 0.07,
    init() {
      // Disabled - Shopify handles scroll natively
    },
    getY() { return W.scrollY; }
  };

  // ─── CURSOR ───────────────────────────────────────────────────────────────
  // The custom cursor must render above EVERYTHING — including <dialog>
  // elements promoted to the browser's Top Layer (cart drawer, modals, etc.).
  // A high z-index alone cannot beat the Top Layer; the cursor itself must
  // either live in the Top Layer (popover API) or be re-attached into the
  // currently open dialog. We do both, with graceful fallback.
  const Cursor = {
    x: 0, y: 0, tx: W.innerWidth/2, ty: W.innerHeight/2,
    rx: W.innerWidth/2, ry: W.innerHeight/2,
    init() {
      if (isMobile()) return;
      const s = D.createElement('style');
      s.textContent = `
        #at-cursor-host{position:fixed;inset:0;width:100vw;height:100vh;
          pointer-events:none;background:transparent;border:0;padding:0;margin:0;
          overflow:visible;display:block;color:inherit;}
        #at-cursor-host:not(:popover-open){display:block;}
        #at-cursor-host::backdrop{display:none;background:transparent;}
        #at-dot{position:fixed;top:0;left:0;width:12px;height:12px;
          background:#95511D;border-radius:50%;pointer-events:none;z-index:2147483647;
          transform:translate(-50%,-50%);opacity:0;
          will-change:transform;
          box-shadow:0 0 0 2px rgba(149,81,29,.3);
          transition:opacity .3s,width .35s cubic-bezier(0.22,1,0.36,1),
          height .35s cubic-bezier(0.22,1,0.36,1),box-shadow .3s;}
        #at-ring{position:fixed;top:0;left:0;width:44px;height:44px;
          border:2px solid rgba(149,81,29,.6);border-radius:50%;pointer-events:none;
          z-index:2147483646;transform:translate(-50%,-50%);opacity:0;
          will-change:transform;
          transition:opacity .3s,width .5s cubic-bezier(0.22,1,0.36,1),
          height .5s cubic-bezier(0.22,1,0.36,1),border-color .3s,background .3s;}
        #at-ring.lbl{width:84px;height:84px;background:#1a1a18;border-color:#1a1a18;
          display:flex;align-items:center;justify-content:center;}
        .at-cl{position:absolute;color:#f5f0eb;font-size:9px;font-weight:700;
          letter-spacing:.12em;text-transform:uppercase;white-space:nowrap;
          opacity:0;transition:opacity .2s;font-family:inherit;}
        #at-ring.lbl .at-cl{opacity:1;}
        *,html,body{cursor:none!important;}
        dialog,[role="dialog"],.cart-drawer,.modal,.drawer{cursor:none!important;}
        dialog *,[role="dialog"] *,.cart-drawer *,.modal *,.drawer *{cursor:none!important;}
      `;
      D.head.appendChild(s);

      // ── Build cursor in a popover host so it lives in the Top Layer ──
      const host = D.createElement('div');
      host.id = 'at-cursor-host';
      // popover="manual" → we control show/hide; element is promoted to Top Layer when shown.
      host.setAttribute('popover', 'manual');

      const dot  = D.createElement('div'); dot.id  = 'at-dot';
      const ring = D.createElement('div'); ring.id = 'at-ring';
      const lbl  = D.createElement('span'); lbl.className = 'at-cl';
      ring.appendChild(lbl);
      host.appendChild(dot);
      host.appendChild(ring);
      D.body.appendChild(host);

      const supportsPopover = typeof host.showPopover === 'function';

      const promote = () => {
        if (!supportsPopover) return;
        try { host.hidePopover(); } catch(e) {}
        try { host.showPopover(); } catch(e) {}
      };

      // Initial promote into Top Layer
      promote();

      // ── Re-promote whenever a dialog opens (browser stacks Top Layer
      //    by show-order; newest shown wins, so we re-show our cursor). ──
      const onDialogChange = () => {
        // Defer to next frame so the dialog finishes its open transition
        requestAnimationFrame(promote);
      };

      // Hook native HTMLDialogElement.showModal/show to re-promote afterwards
      if (W.HTMLDialogElement) {
        const origShowModal = HTMLDialogElement.prototype.showModal;
        const origShow      = HTMLDialogElement.prototype.show;
        HTMLDialogElement.prototype.showModal = function() {
          const r = origShowModal.apply(this, arguments);
          onDialogChange();
          return r;
        };
        HTMLDialogElement.prototype.show = function() {
          const r = origShow.apply(this, arguments);
          onDialogChange();
          return r;
        };
      }

      // Watch for non-native drawers (Craft theme uses custom elements
      // and toggles `open` / `aria-hidden` / class names on overlays).
      const mo = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes') {
            const a = m.attributeName;
            if (a === 'open' || a === 'aria-hidden' || a === 'aria-modal' ||
                a === 'class' || a === 'hidden' || a === 'data-state') {
              onDialogChange();
              return;
            }
          } else if (m.type === 'childList') {
            for (const n of m.addedNodes) {
              if (n.nodeType === 1) { onDialogChange(); return; }
            }
          }
        }
      });
      mo.observe(D.body, {
        attributes: true,
        attributeFilter: ['open','aria-hidden','aria-modal','class','hidden','data-state'],
        subtree: true,
        childList: true
      });

      // Re-promote on focus changes (drawer often steals focus)
      D.addEventListener('focusin', onDialogChange);

      // ── Cursor movement & interactions (unchanged behaviour) ──
      D.addEventListener('mousemove', e => {
        this.tx=e.clientX; this.ty=e.clientY;
        dot.style.left=e.clientX+'px'; dot.style.top=e.clientY+'px';
        dot.style.opacity=ring.style.opacity='1';
      });
      D.addEventListener('mouseleave',()=>{ dot.style.opacity=ring.style.opacity='0'; });

      // Hover handlers must also catch elements inside opened dialogs.
      // We delegate via document-level events instead of binding once at init.
      const isInteractive = (t) => t && t.closest && t.closest('a,button,[data-cursor-hover],[data-cursor-text]');

      D.addEventListener('mouseover', e => {
        const el = isInteractive(e.target);
        if (!el) return;
        if (el.hasAttribute('data-cursor-text')) {
          lbl.textContent = el.dataset.cursorText;
          ring.classList.add('lbl');
          dot.style.opacity = '0';
        } else {
          dot.style.width = dot.style.height = '6px';
          dot.style.boxShadow = '0 0 0 4px rgba(149,81,29,.2)';
          ring.style.width = ring.style.height = '58px';
          ring.style.borderColor = 'rgba(149,81,29,.8)';
        }
      }, true);

      D.addEventListener('mouseout', e => {
        const el = isInteractive(e.target);
        if (!el) return;
        if (el.hasAttribute('data-cursor-text')) {
          ring.classList.remove('lbl');
          dot.style.opacity = '1';
        } else {
          dot.style.width = dot.style.height = '12px';
          dot.style.boxShadow = '0 0 0 2px rgba(149,81,29,.3)';
          ring.style.width = ring.style.height = '44px';
          ring.style.borderColor = 'rgba(149,81,29,.6)';
        }
      }, true);

      const tick = () => {
        this.rx=lerp(this.rx,this.tx,.09); this.ry=lerp(this.ry,this.ty,.09);
        ring.style.left=this.rx+'px'; ring.style.top=this.ry+'px';
        requestAnimationFrame(tick);
      };
      tick();
    }
  };

  // ─── CINEMATIC SCROLL ENGINE ─────────────────────────────────────────────
  const Cinema = {
    scenes: [], _rafId: null,
    init() {
      D.querySelectorAll('[data-scene]').forEach(el => {
        this.scenes.push({ el, type: el.dataset.scene });
      });
      if (!this.scenes.length) return;
      W.addEventListener('scroll', () => {
        if (this._rafId) return;
        this._rafId = requestAnimationFrame(() => {
          this._update();
          this._rafId = null;
        });
      }, { passive: true });
      this._update();
    },
    _update() {
      const sy = W.scrollY;
      const wh = W.innerHeight;
      this.scenes.forEach(({ el, type }) => {
        const rect = el.getBoundingClientRect();
        const progress = clamp(1 - (rect.top / wh), 0, 1);
        const entered = rect.top < wh && rect.bottom > 0;

        if (type === 'fade-wipe') {
          if (entered) {
            el.style.opacity = clamp(progress * 2, 0, 1);
            el.style.clipPath = `inset(${clamp((1-progress)*100,0,100)}% 0 0 0)`;
          }
        } else if (type === 'scale-reveal') {
          if (entered) {
            const s = map(progress, 0, 0.5, 1.1, 1);
            el.style.transform = `scale(${s})`;
            el.style.opacity = clamp(progress * 2.5, 0, 1);
          }
        } else if (type === 'horizontal') {
          if (entered) {
            const x = map(progress, 0, 1, 60, 0);
            el.style.transform = `translateX(${x}px)`;
            el.style.opacity = clamp(progress * 2, 0, 1);
          }
        } else if (type === 'cinematic-text') {
          if (entered) {
            const blur = map(progress, 0, 0.6, 20, 0);
            const y = map(progress, 0, 0.5, 40, 0);
            el.style.filter = `blur(${blur}px)`;
            el.style.transform = `translateY(${y}px)`;
            el.style.opacity = clamp(progress * 2, 0, 1);
          }
        } else if (type === 'pin-zoom') {
          const pinProgress = clamp((wh - rect.top) / (wh + el.offsetHeight), 0, 1);
          const s = map(pinProgress, 0, 1, 0.85, 1.15);
          el.style.transform = `scale(${s})`;
          el.style.opacity = Math.sin(pinProgress * Math.PI);
        }
      });
    }
  };

  // ─── SCROLL REVEAL ────────────────────────────────────────────────────────
  const Reveal = {
    init() {
      const FX = {
        'fade-up':   'opacity:0;transform:translateY(70px)',
        'fade-down': 'opacity:0;transform:translateY(-50px)',
        'fade-left': 'opacity:0;transform:translateX(70px)',
        'fade-right':'opacity:0;transform:translateX(-70px)',
        'fade':      'opacity:0',
        'scale-up':  'opacity:0;transform:scale(0.82)',
        'blur-up':   'opacity:0;transform:translateY(50px);filter:blur(16px)',
        'blur':      'opacity:0;filter:blur(20px)',
        'slide-up':  'opacity:0;transform:translateY(110px)',
        'rotate-in': 'opacity:0;transform:rotate(-12deg) scale(0.88)',
        'clip-x':    'clip-path:inset(0 100% 0 0);opacity:0',
        'clip-y':    'clip-path:inset(100% 0 0 0);opacity:0',
        'skew-up':   'opacity:0;transform:translateY(60px) skewY(5deg)',
      };
      D.querySelectorAll('[data-reveal]').forEach(el => {
        const fx = el.dataset.reveal||'fade-up';
        const init = FX[fx]||FX['fade-up'];
        const delay = parseFloat(el.dataset.delay||0);
        const dur = parseFloat(el.dataset.duration||0.9);
        init.split(';').forEach(r => {
          const [p,v]=r.split(':');
          if(p&&v) el.style[p.trim().replace(/-([a-z])/g,(_,l)=>l.toUpperCase())]=v.trim();
        });
        el.style.transition=`opacity ${dur}s cubic-bezier(0.22,1,0.36,1) ${delay}s,transform ${dur}s cubic-bezier(0.22,1,0.36,1) ${delay}s,filter ${dur}s cubic-bezier(0.22,1,0.36,1) ${delay}s,clip-path ${dur}s cubic-bezier(0.22,1,0.36,1) ${delay}s`;
        observe(el,()=>{
          el.style.opacity='1'; el.style.transform='none';
          el.style.filter='none'; el.style.clipPath='none';
        });
      });
    }
  };

  // ─── TEXT SPLIT ───────────────────────────────────────────────────────────
  const Split = {
    init() {
      D.querySelectorAll('[data-split]').forEach(el => {
        const type = el.dataset.split;
        const text = el.textContent.trim();
        const delay = parseFloat(el.dataset.delay||0);
        const stagger = parseFloat(el.dataset.stagger||0.022);

        if (type==='chars') {
          el.innerHTML = text.split('').map((c,i) => {
            if(c===' ') return '<span style="display:inline-block;width:.28em"> </span>';
            const d=(delay+i*stagger).toFixed(3);
            return `<span class="at-ch" style="display:inline-block;opacity:0;transform:translateY(115%) skewY(10deg);transition:opacity .65s cubic-bezier(0.22,1,0.36,1) ${d}s,transform .65s cubic-bezier(0.22,1,0.36,1) ${d}s;will-change:transform">${c}</span>`;
          }).join('');
          observe(el,()=>{
            el.querySelectorAll('.at-ch').forEach(s=>{s.style.opacity='1';s.style.transform='translateY(0) skewY(0)';});
          });

        } else if (type==='words') {
          el.innerHTML = text.split(' ').map((w,i) => {
            const d=(delay+i*stagger*3).toFixed(3);
            return `<span style="display:inline-block;overflow:hidden;vertical-align:bottom"><span class="at-wd" style="display:inline-block;opacity:0;transform:translateY(105%);transition:opacity .8s cubic-bezier(0.22,1,0.36,1) ${d}s,transform .8s cubic-bezier(0.22,1,0.36,1) ${d}s">${w}</span></span> `;
          }).join('');
          observe(el,()=>{
            el.querySelectorAll('.at-wd').forEach(s=>{s.style.opacity='1';s.style.transform='translateY(0)';});
          });

        } else if (type==='lines') {
          el.innerHTML = text.split('\n').map((l,i) => {
            const d=(delay+i*0.15).toFixed(3);
            return `<div style="overflow:hidden"><span class="at-ln" style="display:block;opacity:0;transform:translateY(100%);transition:opacity .9s cubic-bezier(0.22,1,0.36,1) ${d}s,transform .9s cubic-bezier(0.22,1,0.36,1) ${d}s">${l}</span></div>`;
          }).join('');
          observe(el,()=>{
            el.querySelectorAll('.at-ln').forEach(s=>{s.style.opacity='1';s.style.transform='translateY(0)';});
          });
        }
      });
    }
  };

  // ─── TEXT SCRAMBLE ────────────────────────────────────────────────────────
  const Scramble = {
    run(el, dur) {
      const orig = el.textContent;
      let f=0; const total=Math.ceil((dur||1200)/40);
      let lastTime=0;
      const tick=(ts)=>{
        if(ts-lastTime>40){
          el.textContent=orig.split('').map((c,i)=>{
            if(c===' ') return ' ';
            return f/total>i/orig.length?c:CHARS[Math.floor(Math.random()*CHARS.length)];
          }).join('');
          f++;
          lastTime=ts;
        }
        if(f<=total) requestAnimationFrame(tick);
        else el.textContent=orig;
      };
      requestAnimationFrame(tick);
    },
    init() {
      D.querySelectorAll('[data-scramble]').forEach(el => {
        const dur = parseInt(el.dataset.scrambleDur||600);
        if(el.dataset.scramble==='scroll') observe(el,()=>this.run(el,dur));
        else el.addEventListener('mouseenter',()=>this.run(el,dur));
      });
    }
  };

  // ─── 3D TILT ──────────────────────────────────────────────────────────────
  const Tilt = {
    init() {
      if(isMobile()) return;
      D.querySelectorAll('[data-tilt]').forEach(el => {
        const str=parseFloat(el.dataset.tilt||10);
        const glare=el.hasAttribute('data-glare');
        let gEl,lx=0,ly=0,raf;
        el.style.cssText+='transform-style:preserve-3d;will-change:transform;';
        if(glare){
          el.style.position='relative';el.style.overflow='hidden';
          gEl=D.createElement('div');
          gEl.style.cssText='position:absolute;inset:0;pointer-events:none;z-index:2;opacity:0;transition:opacity .4s';
          el.appendChild(gEl);
        }
        el.addEventListener('mousemove',e=>{
          const r=el.getBoundingClientRect();
          const nx=((e.clientX-r.left)/r.width-.5)*2;
          const ny=((e.clientY-r.top)/r.height-.5)*2;
          cancelAnimationFrame(raf);
          const tick=()=>{
            lx=lerp(lx,nx,.14);ly=lerp(ly,ny,.14);
            el.style.transform=`perspective(900px) rotateY(${lx*str}deg) rotateX(${-ly*str}deg) scale3d(1.02,1.02,1.02)`;
            if(gEl){gEl.style.opacity='.5';gEl.style.background=`radial-gradient(circle at ${(nx+1)*50}% ${(ny+1)*50}%,rgba(255,255,255,.18),transparent 65%)`;}
            if(Math.abs(lx-nx)>.001||Math.abs(ly-ny)>.001) raf=requestAnimationFrame(tick);
          };
          tick();
        });
        el.addEventListener('mouseleave',()=>{
          cancelAnimationFrame(raf);
          el.style.transition='transform .7s cubic-bezier(0.22,1,0.36,1)';
          el.style.transform='perspective(900px) rotateY(0) rotateX(0) scale3d(1,1,1)';
          if(gEl) gEl.style.opacity='0';
          lx=ly=0;
          setTimeout(()=>el.style.transition='',700);
        });
      });
    }
  };

  // ─── MAGNETIC ─────────────────────────────────────────────────────────────
  const Magnet = {
    init() {
      if(isMobile()) return;
      D.querySelectorAll('[data-magnet]').forEach(el => {
        const str=parseFloat(el.dataset.magnet||.45);
        const inner=el.querySelector('[data-magnet-inner]')||el;
        el.addEventListener('mousemove',e=>{
          const r=el.getBoundingClientRect();
          const dx=(e.clientX-r.left-r.width/2)*str;
          const dy=(e.clientY-r.top-r.height/2)*str;
          el.style.transition='transform .2s cubic-bezier(0.22,1,0.36,1)';
          el.style.transform=`translate(${dx}px,${dy}px)`;
          inner.style.transform=`translate(${dx*.3}px,${dy*.3}px)`;
        });
        el.addEventListener('mouseleave',()=>{
          el.style.transition=inner.style.transition='transform .7s cubic-bezier(0.22,1,0.36,1)';
          el.style.transform=inner.style.transform='translate(0,0)';
        });
      });
    }
  };

  // ─── PARALLAX ─────────────────────────────────────────────────────────────
  const Parallax = {
    _rafId: null,
    init() {
      const els=[...D.querySelectorAll('[data-parallax]')];
      if(!els.length) return;
      const update=()=>{
        els.forEach(el=>{
          const speed=parseFloat(el.dataset.parallax||.3);
          const rect=el.getBoundingClientRect();
          const center=rect.top+rect.height/2-W.innerHeight/2;
          el.style.transform=`translateY(${center*speed*-.8}px)`;
        });
      };
      W.addEventListener('scroll',()=>{
        if(this._rafId) return;
        this._rafId=requestAnimationFrame(()=>{
          update();
          this._rafId=null;
        });
      },{passive:true});
      update();
    }
  };

  // ─── COUNTER ──────────────────────────────────────────────────────────────
  const Counter = {
    init() {
      D.querySelectorAll('[data-count]').forEach(el => {
        const target=parseFloat(el.dataset.count);
        const dur=parseFloat(el.dataset.duration||2000);
        const prefix=el.dataset.prefix||'';
        const suffix=el.dataset.suffix||'';
        const dec=parseInt(el.dataset.decimals||0);
        let done=false;
        observe(el,()=>{
          if(done) return; done=true;
          const s=performance.now();
          const tick=ts=>{
            const p=clamp((ts-s)/dur,0,1),ease=1-Math.pow(1-p,4);
            el.textContent=prefix+(ease*target).toFixed(dec)+suffix;
            if(p<1) requestAnimationFrame(tick);
            else el.textContent=prefix+target.toFixed(dec)+suffix;
          };
          requestAnimationFrame(tick);
        });
      });
    }
  };

  // ─── LINE REVEAL ──────────────────────────────────────────────────────────
  const Line = {
    init() {
      D.querySelectorAll('[data-line]').forEach(el => {
        const color=el.dataset.lineColor||'#95511D';
        const delay=parseFloat(el.dataset.delay||0);
        el.style.cssText+='position:relative;display:inline-block;';
        const line=D.createElement('span');
        line.style.cssText=`position:absolute;bottom:-2px;left:0;width:0;height:1.5px;background:${color};border-radius:99px;display:block;transition:width .8s cubic-bezier(0.22,1,0.36,1) ${delay}s;`;
        el.appendChild(line);
        observe(el,()=>{ line.style.width='100%'; });
      });
    }
  };

  // ─── NOISE (DISABLED) ─────────────────────────────────────────────────────
  const Noise = {
    init() {
      return; // disabled
      if(!D.querySelector('[data-noise]')) return;
      const c=D.createElement('canvas'); c.width=c.height=200;
      const ctx=c.getContext('2d');
      const regen=()=>{
        const img=ctx.createImageData(200,200);
        for(let i=0;i<img.data.length;i+=4){
          const v=Math.random()*255;
          img.data[i]=img.data[i+1]=img.data[i+2]=v; img.data[i+3]=14;
        }
        ctx.putImageData(img,0,0);
      };
      regen();
      const ov=D.createElement('div');
      ov.style.cssText='position:fixed;inset:0;z-index:99990;pointer-events:none;';
      ov.style.backgroundImage=`url(${c.toDataURL()})`;
      ov.style.backgroundSize='200px 200px';
      D.body.appendChild(ov);
      let f=0;
      const anim=()=>{
        if(++f%2===0){ regen(); ov.style.backgroundImage=`url(${c.toDataURL()})`; ov.style.backgroundPosition=`${rand(0,100)}% ${rand(0,100)}%`; }
        requestAnimationFrame(anim);
      };
      anim();
    }
  };

  // ─── MARQUEE ──────────────────────────────────────────────────────────────
  const Marquee = {
    init() {
      D.querySelectorAll('[data-marquee]').forEach(el => {
        const speed=parseFloat(el.dataset.marquee||40);
        const dir=el.dataset.marqueeDir==='right'?1:-1;
        const content=el.innerHTML;
        el.innerHTML=content+content+content;
        el.style.cssText+='display:flex;overflow:hidden;white-space:nowrap;';
        Array.from(el.children).forEach(c=>c.style.flexShrink='0');
        let x=0,paused=false;
        const w=el.scrollWidth/3;
        const tick=()=>{
          if(!paused){ x+=dir*(speed/60); if(dir<0&&Math.abs(x)>=w)x+=w; if(dir>0&&x>=0)x-=w; el.style.transform=`translateX(${x}px)`; }
          requestAnimationFrame(tick);
        };
        tick();
        el.addEventListener('mouseenter',()=>paused=true);
        el.addEventListener('mouseleave',()=>paused=false);
      });
    }
  };

  // ─── HOVER LIFT ───────────────────────────────────────────────────────────
  const HoverLift = {
    init() {
      D.querySelectorAll('[data-lift]').forEach(el => {
        const y=parseFloat(el.dataset.lift||8);
        el.style.transition='transform .4s cubic-bezier(0.22,1,0.36,1),box-shadow .4s ease';
        el.addEventListener('mouseenter',()=>{ el.style.transform=`translateY(-${y}px)`; });
        el.addEventListener('mouseleave',()=>{ el.style.transform='translateY(0)'; });
      });
    }
  };

  // ─── STAGGER ──────────────────────────────────────────────────────────────
  const Stagger = {
    init() {
      D.querySelectorAll('[data-stagger]').forEach(parent => {
        const s=parseFloat(parent.dataset.stagger||.08);
        const fx=parent.dataset.staggerReveal||'fade-up';
        const base=parseFloat(parent.dataset.staggerBase||0);
        Array.from(parent.children).forEach((c,i)=>{ c.dataset.reveal=fx; c.dataset.delay=(base+i*s).toFixed(3); });
        Reveal.init();
      });
    }
  };

  // ─── PROGRESS ─────────────────────────────────────────────────────────────
  const Progress = {
    init() {
      if(!D.querySelector('[data-progress]')) return;
      const bar=D.createElement('div');
      bar.style.cssText='position:fixed;top:0;left:0;height:2px;width:0;background:linear-gradient(90deg,#95511D,#d4844a);z-index:2147483647;pointer-events:none;transition:width .1s linear;';
      D.body.appendChild(bar);
      W.addEventListener('scroll',()=>{ bar.style.width=clamp(W.scrollY/(D.documentElement.scrollHeight-W.innerHeight)*100,0,100)+'%'; },{passive:true});
    }
  };

  // ─── PAGE TRANSITION ──────────────────────────────────────────────────────
  const PageTransition = {
    init() {
      if(!D.querySelector('[data-page-transition]')) return;
      const veil=D.createElement('div');
      veil.style.cssText='position:fixed;inset:0;z-index:999999;pointer-events:none;background:#1a1a18;transform:scaleY(0);transform-origin:bottom;transition:transform .55s cubic-bezier(0.76,0,0.24,1);';
      D.body.appendChild(veil);
      D.querySelectorAll('a[href]:not([target="_blank"]):not([href^="#"]):not([href^="mailto"])').forEach(link=>{
        link.addEventListener('click',e=>{
          const href=link.getAttribute('href');
          if(!href||href.startsWith('javascript')) return;
          e.preventDefault();
          veil.style.transform='scaleY(1)';
          setTimeout(()=>W.location.href=href,550);
        });
      });
      W.addEventListener('pageshow',()=>{ veil.style.transformOrigin='top'; setTimeout(()=>veil.style.transform='scaleY(0)',50); });
    }
  };

  // ─── VIDEO AMBIENT ────────────────────────────────────────────────────────
  const VideoAmbient = {
    init() {
      D.querySelectorAll('[data-ambient]').forEach(video => {
        video.setAttribute('autoplay','');
        video.setAttribute('muted','');
        video.setAttribute('loop','');
        video.setAttribute('playsinline','');
        video.style.cssText+='object-fit:cover;width:100%;height:100%;';
      });
    }
  };

  // ─── SCROLL PROGRESS REVEAL ───────────────────────────────────────────────
  const ScrollProgress = {
    init() {
      D.querySelectorAll('[data-scroll-progress]').forEach(el => {
        const prop = el.dataset.scrollProgress||'opacity';
        const from = parseFloat(el.dataset.scrollFrom||0);
        const to = parseFloat(el.dataset.scrollTo||1);
        W.addEventListener('scroll',()=>{
          const rect=el.getBoundingClientRect();
          const p=clamp(1-rect.top/W.innerHeight,0,1);
          const v=map(p,0,1,from,to);
          el.style[prop]=v;
        },{passive:true});
      });
    }
  };

  // ─── INIT ─────────────────────────────────────────────────────────────────
  const init = () => {
    Smooth.init();
    Cursor.init();
    Cinema.init();
    Reveal.init();
    Split.init();
    Scramble.init();
    Tilt.init();
    Magnet.init();
    Parallax.init();
    Counter.init();
    Line.init();
    Noise.init();
    Marquee.init();
    HoverLift.init();
    Stagger.init();
    Progress.init();
    PageTransition.init();
    VideoAmbient.init();
    ScrollProgress.init();
  };

  D.readyState==='loading' ? D.addEventListener('DOMContentLoaded',init) : init();
  W.Atelier = { Smooth, Cursor, Cinema, Reveal, Split, Scramble, Tilt, Magnet, Parallax, Counter, Line, Noise, Marquee, Stagger, Progress, observe };

})(window, document);
